import { StyledProps } from '@front10';
import VisuallyHidden from '@reach/visually-hidden';
import React, { useContext } from 'react';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import { getCorrectCurrencySettings } from '../../../../helpers/utilsPricing';

interface Props {
  /**
   * values of miles
   */
  redemption: $TSFixMe;
  /**
   * Custom label key for milesPromo
   */
  milesPromoLabelKey: string;
  /**
   * milesSymbol value
   */
  milesSymbol: string;
  /**
   * milesDisclaimer value
   */
  milesDisclaimer: string;
  /**
   * CurrencySymbol value
   */
  currencySymbol: string;
  /**
   * taxAmountSymbol value
   */
  taxAmountSymbol: string;
  /**
   * milesPromoStyles custom props
   */
  milesPromoStyles: $TSFixMe;
  /**
   * formattedAmountStyles custom props
   */
  formattedAmountStyles: $TSFixMe;
  /**
   * formattedTaxAmountStyles custom props
   */
  formattedTaxAmountStyles: $TSFixMe;
  /**
   * milesSymbolStyles custom props
   */
  milesSymbolStyles: $TSFixMe;
  /**
   * milesDisclaimerStyles custom props
   */
  milesDisclaimerStyles: $TSFixMe;
  /**
   * currencySymbolStyles custom props
   */
  currencySymbolStyles: $TSFixMe;
  /**
   * taxAmountSymbolStyles custom props
   */
  taxAmountSymbolStyles: $TSFixMe;
  /**
   * amountStyles custom props
   */
  amountStyles: $TSFixMe;
  /**
   * taxAmountStyles custom props
   */
  taxAmountStyles: $TSFixMe;
  /**
   * show milesPromo
   */
  showMilesPromo: boolean;
  /**
   * show showFormattedAmount
   */
  showFormattedAmount: boolean;
  /**
   * show showFormattedTaxAmount
   */
  showFormattedTaxAmount: boolean;
  /**
   * show showTaxAmountSymbol
   */
  showTaxAmountSymbol: boolean;
  /**
   * show showCurrencySymbol
   */
  showCurrencySymbol: boolean;
  /**
   * show showMilesDisclaimer
   */
  showMilesDisclaimer: boolean;
  /**
   * show showMilesDisclaimer
   */
  showMilesSymbol: boolean;
  /**
   * Advanced configuration to currencySettings
   */
  advancedCurrencySettings: $TSFixMe;
  /**
   * Define in what order should be displayed the currency
   */
  currencyInFront: boolean;
  /**
   * Currency value to display
   */
  currencyToDisplay: string;
  /**
   * Currency Code
   */
  currencyCode: string;
  /**
   * currencyToDisplayStyles props for using into withStyle.
   */
  currencyToDisplayStyles: $TSFixMe;
  /**
   * Show currency code.
   */
  showCurrencyCode: boolean;
  /**
   * currencyCodeStyles props for using into withStyle.
   */
  currencyCodeStyles: $TSFixMe;
  /**
   * Define if show the currency symbol as a <sup> or <span> tag
   */
  showCurrencySymbolSub: boolean;
  /**
   * Show label miles disclaimerd menssage
   */
  showLabelMilesDisclaimerdMenssage?: boolean;
  showDecimalSeparator?: boolean;
  decimalSeparator?: string;
  showShortMiles?: boolean;
  shortenedFormattedTotalPrice?: string;
  id: string;
  showUnroundedTaxAmount?: boolean;
  showBaseMilesOrPointsDisclaimer?: boolean;
}

function FareItemMiles(props: StyledProps<Props>) {
  const {
    redemption,
    milesPromoLabelKey,
    milesSymbol,
    milesDisclaimer,
    currencySymbol,
    taxAmountSymbol,
    milesPromoStyles,
    formattedAmountStyles,
    formattedTaxAmountStyles,
    milesSymbolStyles,
    milesDisclaimerStyles,
    currencySymbolStyles,
    taxAmountSymbolStyles,
    amountStyles,
    taxAmountStyles,
    showMilesPromo,
    showFormattedAmount,
    showFormattedTaxAmount,
    showTaxAmountSymbol,
    showCurrencySymbol,
    showMilesDisclaimer,
    showMilesSymbol,
    advancedCurrencySettings,
    currencyInFront,
    currencyToDisplay,
    currencyCode,
    currencyToDisplayStyles,
    showCurrencyCode,
    currencyCodeStyles,
    showCurrencySymbolSub,
    showLabelMilesDisclaimerdMenssage,
    showDecimalSeparator = true,
    showShortMiles,
    shortenedFormattedTotalPrice,
    showUnroundedTaxAmount,
    showBaseMilesOrPointsDisclaimer,
    id
  } = props;
  const correctCurrencySettings = getCorrectCurrencySettings(
    {
      currencyToDisplay,
      currencyInFront
    },
    advancedCurrencySettings,
    currencyCode
  );
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const { formattedAmount, formattedTaxAmount, taxAmount } = redemption;

  // currencyToDisplay atom just render wethever you pass: "Dog"
  const renderCurrencyToDisplay = (
    currencyToDisplay: string,
    currencyToDisplayStyles: $TSFixMe,
    isInFront: boolean
  ) => (
    <Text
      paddingRight={
        isInFront && currencyToDisplay.charAt(currencyToDisplay.length - 1) !== ' ' ? '1' : '0'
      }
      paddingLeft={!isInFront && currencyToDisplay.charAt(0) !== ' ' ? '1' : '0'}
      {...currencyToDisplayStyles}
      style={{ whiteSpace: 'pre', ...currencyToDisplayStyles?.style }}
      floatLeft
    >
      {currencyToDisplay}
    </Text>
  );

  const tag = showCurrencySymbolSub ? 'sup' : 'span';
  const Tag = tag;

  const getFormattedAmount = (formattedAmount: string, showDecimalSeparator: boolean) => {
    let newFormattedAmount = formattedAmount;
    if (showDecimalSeparator === false) {
      if (props.decimalSeparator !== undefined && props.decimalSeparator !== null) {
        if (props.decimalSeparator === '') {
          const index = formattedAmount.lastIndexOf('.');
          if (index !== -1) {
            return newFormattedAmount.substring(0, index);
          }
        } else {
          const index = formattedAmount.lastIndexOf(props.decimalSeparator);
          if (index !== -1) {
            return newFormattedAmount.substring(0, index);
          }
        }
      }
    }
    return formattedAmount;
  };

  const currency = (
    <>
      {showCurrencyCode && (
        <Text
          paddingRight={correctCurrencySettings.currencyInFront ? '1' : '0'}
          paddingLeft={!correctCurrencySettings.currencyInFront ? '1' : '0'}
          {...currencyCodeStyles}
          floatLeft
        >
          {currencyCode}
        </Text>
      )}
      {showCurrencySymbol && (
        <Text {...currencySymbolStyles} floatLeft>
          <Tag className={`fr-${tag}`}>{currencySymbol}</Tag>
        </Text>
      )}
    </>
  );

  const [amountClassName, amountStyle] = useStyles(amountStyles, { direction, theme });
  const [taxAmountClassName, taxAmountStyle] = useStyles(taxAmountStyles, { direction, theme });

  return (
    <Box className={className} style={style} id={id}>
      {showMilesPromo && <Text {...milesPromoStyles}>{translate(milesPromoLabelKey, 'From')}</Text>}
      {showFormattedAmount && (
        <Box className={`fr-clearfix ${amountClassName}`} style={amountStyle}>
          <Text floatLeft {...formattedAmountStyles}>
            {showShortMiles
              ? shortenedFormattedTotalPrice
              : getFormattedAmount(formattedAmount, showDecimalSeparator)}
          </Text>
          {showMilesSymbol && (
            <Text floatLeft {...milesSymbolStyles}>
              {translate(`cmp-${milesSymbol}`, milesSymbol)}
            </Text>
          )}
          {showBaseMilesOrPointsDisclaimer && (
            <Text {...milesDisclaimerStyles}>{milesDisclaimer}</Text>
          )}
        </Box>
      )}
      {showFormattedTaxAmount && (
        <Box className={`fr-clearfix ${taxAmountClassName}`} style={taxAmountStyle}>
          {showTaxAmountSymbol && (
            <Text {...taxAmountSymbolStyles} floatLeft>
              {taxAmountSymbol}
            </Text>
          )}
          {/* currencyToDisplay in Front */}
          {correctCurrencySettings.currencyToDisplay && correctCurrencySettings.currencyInFront
            ? renderCurrencyToDisplay(
                correctCurrencySettings.currencyToDisplay,
                currencyToDisplayStyles,
                correctCurrencySettings.currencyInFront
              )
            : correctCurrencySettings.currencyToDisplay === '' &&
              correctCurrencySettings.currencyInFront &&
              currency}
          {!showUnroundedTaxAmount ? (
            <Text {...formattedTaxAmountStyles} floatLeft>
              {formattedTaxAmount}
            </Text>
          ) : (
            <Text {...formattedTaxAmountStyles} floatLeft>
              {taxAmount}
            </Text>
          )}
          {/* currencyToDisplay in Back */}
          {correctCurrencySettings.currencyToDisplay && !correctCurrencySettings.currencyInFront
            ? renderCurrencyToDisplay(
                correctCurrencySettings.currencyToDisplay,
                currencyToDisplayStyles,
                correctCurrencySettings.currencyInFront
              )
            : correctCurrencySettings.currencyToDisplay === '' &&
              !correctCurrencySettings.currencyInFront &&
              currency}
          {showMilesDisclaimer && !showBaseMilesOrPointsDisclaimer && (
            <>
              <Text {...milesDisclaimerStyles} floatLeft>
                {milesDisclaimer}
              </Text>
              {showLabelMilesDisclaimerdMenssage && (
                <VisuallyHidden>
                  {' '}
                  {translate('cmp-price-disclaimer', 'Price subject to changes')}
                </VisuallyHidden>
              )}
            </>
          )}
          {showBaseMilesOrPointsDisclaimer && showLabelMilesDisclaimerdMenssage && (
            <VisuallyHidden>
              {' '}
              {translate('cmp-price-disclaimer', 'Price subject to changes')}
            </VisuallyHidden>
          )}
        </Box>
      )}
    </Box>
  );
}

FareItemMiles.defaultProps = {
  showCurrencyCode: true,
  advancedCurrencySettings: {},
  currencyInFront: true,
  currencyToDisplay: '',
  redemption: {},
  milesPromoLabelKey: 'fare_promo_label',
  milesSymbol: 'mi',
  milesDisclaimer: '*',
  currencyCode: 'USD',
  currencySymbol: '$',
  taxAmountSymbol: '+',
  milesPromoStyles: {},
  formattedAmountStyles: {},
  formattedTaxAmountStyles: {},
  milesSymbolStyles: {},
  milesDisclaimerStyles: {},
  currencySymbolStyles: {},
  taxAmountSymbolStyles: {},
  amountStyles: {},
  taxAmountStyles: {},
  showMilesPromo: true,
  showFormattedAmount: true,
  showFormattedTaxAmount: true,
  showTaxAmountSymbol: true,
  showCurrencySymbol: true,
  showMilesDisclaimer: true,
  showMilesSymbol: true,
  currencyToDisplayStyles: {},
  currencyCodeStyles: {},
  showCurrencySymbolSub: true,
  showLabelMilesDisclaimerdMenssage: true,
  showUnroundedTaxAmount: false,
  showBaseMilesOrPointsDisclaimer: false
};

export default FareItemMiles;
